// engine-exception: an SLA policy is edited as a targets GRID (priorities × respond/plan/resolve minutes) that writes many sla_target rows at once — not one record's form
import { useEffect, useMemo, useState } from 'react';
import { useCreate, useResourceList, useUpdate } from '../../engine/api/hooks';
import { Badge, EmptyState, ErrorBanner, PageHeader, Skeleton } from '../../engine/design/primitives/Misc';
import { Button } from '../../engine/design/primitives/Button';
import { Field, Input, Select, Switch } from '../../engine/design/primitives/Input';
import { Chip } from '../../engine/design/primitives/Misc';
import { formatMinutes, parseDurationToMinutes } from '../../lib/format';
import { usePriorities } from './lib/catalog';

// SLA policy editor: policy header (name, calendar, warn thresholds, observe company holidays,
// default) + the targets grid. Columns are the tenant's configurable SLA stages (in sort order);
// cells are h:mm; saving upserts sla_target rows per (policy, priority, stage code).

type Row = Record<string, unknown>;

export function SlaPolicyScreen() {
  const policies = useResourceList<Row>('sla-policies', { sort: 'name' }, { limit: 100 });
  const calendars = useResourceList<Row>('business-calendars', { sort: 'name' }, { limit: 100 });
  const stages = useResourceList<Row>('sla-stages', { filter: [{ field: 'is_active', op: 'eq', value: true }], sort: 'sort' }, { limit: 100 });
  const stageList = useMemo(() => stages.rows.map((s) => ({ code: String(s['code']), label: String(s['label']) })), [stages.rows]);
  const priorities = usePriorities();
  const [selected, setSelected] = useState<string | null>(null);
  const policy = policies.rows.find((p) => p.id === selected) ?? policies.rows.find((p) => p['is_default']) ?? policies.rows[0] ?? null;
  const policyId = policy ? String(policy['id']) : null;
  const targets = useResourceList<Row>('sla-targets', { filter: policyId ? [{ field: 'policy_id', op: 'eq', value: policyId }] : undefined }, { limit: 200, enabled: Boolean(policyId) });
  const createTarget = useCreate('sla-targets', { toast: { success: false } });
  const updateTarget = useUpdate('sla-targets', { toast: { success: false } });
  const updatePolicy = useUpdate('sla-policies', { toast: { success: 'Policy saved' } });
  const createPolicy = useCreate('sla-policies', { toast: { success: 'Policy created' } });
  const [grid, setGrid] = useState<Record<string, string>>({});
  const [head, setHead] = useState<{ name: string; calendar_id: string; warn: string; is_default: boolean; observe_company_holidays: boolean }>({ name: '', calendar_id: '', warn: '75, 90', is_default: false, observe_company_holidays: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!policy) return;
    setHead({ name: String(policy['name'] ?? ''), calendar_id: String(policy['calendar_id'] ?? ''), warn: ((policy['warn_pct'] as number[] | null) ?? [75, 90]).join(', '), is_default: Boolean(policy['is_default']), observe_company_holidays: policy['observe_company_holidays'] !== false });
  }, [policy]);
  useEffect(() => {
    const g: Record<string, string> = {};
    for (const t of targets.rows) g[`${String(t['priority_id'])}:${String(t['kind'])}`] = String(t['minutes']);
    setGrid(g);
  }, [targets.rows]);

  const existing = useMemo(() => new Map(targets.rows.map((t) => [`${String(t['priority_id'])}:${String(t['kind'])}`, t])), [targets.rows]);

  const save = async () => {
    if (!policyId) return;
    setSaving(true);
    try {
      await updatePolicy.mutateAsync({ id: policyId, body: { name: head.name, calendar_id: head.calendar_id || null, is_default: head.is_default, observe_company_holidays: head.observe_company_holidays, warn_pct: head.warn.split(',').map((s) => Number(s.trim())).filter((n) => Number.isFinite(n) && n > 0 && n < 100) } });
      for (const p of priorities.rows) {
        for (const k of stageList.map((s) => s.code)) {
          const key = `${p.id}:${k}`;
          const raw = grid[key]?.trim() ?? '';
          if (raw === '') continue;
          const minutes = /^\d+$/.test(raw) ? Number(raw) : parseDurationToMinutes(raw);
          if (!minutes || minutes <= 0) continue;
          const row = existing.get(key);
          if (row) {
            if (Number(row['minutes']) !== minutes) await updateTarget.mutateAsync({ id: String(row['id']), body: { minutes } });
          } else await createTarget.mutateAsync({ policy_id: policyId, priority_id: p.id, kind: k, minutes });
        }
      }
      targets.refetch();
      policies.refetch();
    } finally {
      setSaving(false);
    }
  };

  if (policies.error) return <ErrorBanner error={policies.error} />;
  return (
    <div className="hf-page narrow">
      <PageHeader compact title="SLA policies" subtitle="Targets per priority, counted on the policy's business calendar. Met milestones never un-meet."
        actions={<Button variant="outline" icon="plus" onClick={() => createPolicy.mutate({ name: `Policy ${policies.rows.length + 1}` }, { onSuccess: (r) => { policies.refetch(); setSelected(String((r as Row)['id'])); } })}>New policy</Button>} />
      <div className="hf-chiprow" role="tablist" aria-label="Policies">
        {policies.rows.map((p) => (
          <Chip key={String(p['id'])} role="tab" aria-selected={policyId === p['id']} active={policyId === p['id']} onClick={() => setSelected(String(p['id']))}>{String(p['name'])}{p['is_default'] ? ' · default' : ''}</Chip>
        ))}
      </div>
      {policies.isLoading || priorities.isLoading || stages.isLoading ? <Skeleton lines={6} /> : !policy ? <EmptyState title="No SLA policy" icon="timer" /> : (
        <div className="hf-stack">
          <div className="hf-form-grid">
            <Field label="Name" id="sd-sla-name"><Input id="sd-sla-name" value={head.name} onChange={(e) => setHead({ ...head, name: e.target.value })} /></Field>
            <Field label="Calendar" id="sd-sla-cal"><Select id="sd-sla-cal" value={head.calendar_id} placeholder="24×7" options={calendars.rows.map((c) => ({ value: String(c['id']), label: String(c['name']) }))} onChange={(e) => setHead({ ...head, calendar_id: e.target.value })} /></Field>
            <Field label="Warn at %" id="sd-sla-warn" help="Comma-separated thresholds"><Input id="sd-sla-warn" value={head.warn} onChange={(e) => setHead({ ...head, warn: e.target.value })} /></Field>
            <div style={{ alignSelf: 'end' }}><Switch label="Default policy" checked={head.is_default} onChange={(e) => setHead({ ...head, is_default: e.target.checked })} /></div>
            <div style={{ alignSelf: 'end' }}><Switch label="Observe company holidays" checked={head.observe_company_holidays} onChange={(e) => setHead({ ...head, observe_company_holidays: e.target.checked })} /></div>
          </div>
          <table className="hf-table" aria-label="SLA targets">
            <thead>
              <tr>
                <th>Priority</th>
                {stageList.map((s) => <th key={s.code}>{s.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {priorities.rows.map((p) => (
                <tr key={p.id}>
                  <td><Badge tone={p.level <= 1 ? 'neg' : p.level === 2 ? 'warn' : 'muted'}>{p.name}</Badge></td>
                  {stageList.map((s) => {
                    const key = `${p.id}:${s.code}`;
                    const v = grid[key] ?? '';
                    const mins = v === '' ? null : /^\d+$/.test(v) ? Number(v) : parseDurationToMinutes(v);
                    return (
                      <td key={s.code}>
                        <Input size="sm" numeric value={v} placeholder="h:mm" aria-label={`${p.name} ${s.label} target`} suffix={mins ? formatMinutes(mins) : undefined} onChange={(e) => setGrid({ ...grid, [key]: e.target.value })} />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="hf-row"><Button variant="primary" icon="save" busy={saving} onClick={() => void save()}>Save policy</Button><span className="hf-muted">Minutes or h:mm; blank cells are left as they are.</span></div>
        </div>
      )}
    </div>
  );
}
